/**
 * WelcomeTour — 6-step onboarding modal for first-time approved users.
 *
 * Ported from `dashboard-arcanum/src/components/arcanum/WelcomeTour.tsx`
 * (signup-billing-mvp Phase 3 Session 2, 2026-05-13). Raw hex literals
 * translated to Intelligence Terminal tokens; inline `rgba(...)` for
 * box-shadow glows + accent-tint backgrounds preserved per `apps/web/CLAUDE.md`
 * audit-gate carve-out. Layout values (maxWidth/height/padding) stay inline.
 *
 * Two localStorage keys:
 *   - `arcanum_welcome_tour_seen` — set by Skip/Finish; suppresses the
 *     auto-trigger in `__root.tsx`. Cleared by `resetWelcomeTour()` for
 *     the AccountSettings "Replay tour" path.
 *   - `arcanum_welcome_tour_event_fired` — set when WELCOME_TOUR_SHOWN
 *     has been posted to the API. NEVER cleared by `resetWelcomeTour()`,
 *     so replay paths do NOT re-fire the lifecycle event (spec §3 AC
 *     line 387 — "replays do NOT re-fire"). Divergence #3 in
 *     phases/03-welcome-tour.md captures the why.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { api } from '@/lib/api';
import { posthog } from '@/lib/posthog';

// localStorage keys — strings sit in the module body (not the @theme) since
// they're storage identifiers, not design tokens.
//
// `STORAGE_KEY_PREFIX` is scoped PER USER (2026-05-15 fix): the final key is
// `arcanum_welcome_tour_seen:<userId>`. Cross-user pollution would otherwise
// occur on shared browsers (family PC, sales demo, dev environment cycling
// through test personas) — when user A clicks Skip, user B inheriting the
// browser would never see the tour. The legacy global key
// `arcanum_welcome_tour_seen` is one-shot migrated to the current user on
// app boot (see `migrateLegacyWelcomeTourSeenKey`) — same browser had ONE
// user when the legacy code ran, so attributing it to the currently-active
// user is the safest interpretation.
//
// `EVENT_FIRED_KEY` stays GLOBAL per spec — the `WELCOME_TOUR_SHOWN`
// lifecycle event is once-per-browser (not once-per-user) to prevent
// telemetry noise from multi-user browsers. The "never re-fire on replay"
// invariant from the original design carries forward to the multi-user case:
// telemetry doesn't care which user saw the tour, only that *some* user did.
const STORAGE_KEY_PREFIX = 'arcanum_welcome_tour_seen';
const LEGACY_STORAGE_KEY = 'arcanum_welcome_tour_seen';
const EVENT_FIRED_KEY = 'arcanum_welcome_tour_event_fired';

function getStorageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}:${userId}`;
}

export function hasSeenWelcomeTour(userId: string | undefined | null): boolean {
  if (!userId) return false;
  try {
    return localStorage.getItem(getStorageKey(userId)) === 'true';
  } catch {
    return false;
  }
}
export function markWelcomeTourSeen(userId: string | undefined | null): void {
  if (!userId) return;
  try {
    localStorage.setItem(getStorageKey(userId), 'true');
  } catch {
    // localStorage unavailable (private mode); fall through silently.
  }
}
export function resetWelcomeTour(userId: string | undefined | null): void {
  if (!userId) return;
  try {
    localStorage.removeItem(getStorageKey(userId));
  } catch {
    // Same fallback as above.
  }
}

/**
 * One-shot migration: if the legacy global `arcanum_welcome_tour_seen` key
 * exists (was set by pre-2026-05-15 code that didn't scope per user), copy
 * it onto the currently-active user's per-user key and remove the global.
 * Safe to call repeatedly — no-op if the legacy key is absent. Called from
 * `<RootLayout>`'s auto-trigger useEffect when a user_id is available.
 */
export function migrateLegacyWelcomeTourSeenKey(
  userId: string | undefined | null
): void {
  if (!userId) return;
  try {
    const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (legacy === 'true') {
      localStorage.setItem(getStorageKey(userId), 'true');
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    }
  } catch {
    // Same.
  }
}

/**
 * Orthogonal flag to {@link hasSeenWelcomeTour}. Tracks whether the
 * `WELCOME_TOUR_SHOWN` lifecycle event has been posted ever for this browser.
 * resetWelcomeTour() does NOT reset this — the event is once-per-browser.
 */
export function hasFiredWelcomeTourEvent(): boolean {
  try {
    return localStorage.getItem(EVENT_FIRED_KEY) === 'true';
  } catch {
    return false;
  }
}
export function markWelcomeTourEventFired(): void {
  try {
    localStorage.setItem(EVENT_FIRED_KEY, 'true');
  } catch {
    // Same.
  }
}

type StepDef = {
  eyebrow?: string;
  label?: string;
  headline: string;
  centered?: boolean;
  imageCaption?: string;
  image?: string;
  body?: React.ReactNode;
};

const STEPS: StepDef[] = [
  {
    eyebrow: 'FOUNDING MEMBER',
    headline: 'Welcome to ground zero.',
    centered: true,
    body: (
      <>
        What started as the pursuit to solve a personal problem has become an
        infrastructure. You&apos;re early — and that means a lot. The platform
        you&apos;re using today compounds with every deal you run, every
        assumption you set, every Deal Link you send. A lot is here and live
        now. More is shipping in the weeks ahead.
        <br />
        <br />
        The work this platform does for you is meant to give you back the hours
        that traditional underwriting actually steals — the formatting, the
        rebuilds, the assumption-chasing, the model-prep that sits between you
        and the thinking that closes deals. What you do with those hours is the
        point.
        <br />
        <br />
        If something is missing, tell us. If something could be sharper, tell
        us. Your feedback shapes what gets built next, and the founding team
        reads every line. Our intention is for this platform to match the
        potential of your firm. We&apos;re here to serve.
        <br />
        <br />
        This tour walks you through the four sections you&apos;ll use most. Two
        minutes.
      </>
    ),
  },
  {
    label: 'COCKPIT',
    headline: 'The deal-making command center.',
    image: '/onboarding/tour-cockpit.png',
    body: 'Live market rates. Curated CRE news. Quick calculations. Your pipeline at a glance, plotted on a map. The Cockpit is where every deal-making session begins — context, signals, and your active pipeline in one frame.',
  },
  {
    label: 'BUILDER',
    headline: 'From a sentence to an institutional model.',
    image: '/onboarding/tour-builder.png',
    body: (
      <>
        The Builder has three working surfaces:
        <BulletItem
          label="Workbench"
          text="Describe your deal in plain language. Arcanum parses it into a calibrated institutional model with provenance on every assumption."
        />
        <BulletItem
          label="Signal"
          text={
            'A visual, scannable read of what you just underwrote. Hero metrics, capital stack, returns. Talk to the deal in natural language: "What does this need at a 16% IRR?" — and watch the model respond.'
          }
        />
        <BulletItem
          label="Model View"
          text="The full institutional model rendered natively. Edit any cell, watch every dependent value update in real time. No more fighting Excel."
        />
      </>
    ),
  },
  {
    label: 'PIPELINE',
    headline: 'Track every deal across its lifecycle.',
    image: '/onboarding/tour-pipeline.png',
    body: 'Move deals through the underwriting lifecycle: Analyzing → LOI Submitted → Under Contract → In Market. Watch your pipeline update in real time as you advance deals. Map view shows your active opportunities geographically. Filter by status, asset class, capital partner, or any combination.',
  },
  {
    label: 'DEAL LINK',
    headline: 'Distribute your deal in one click.',
    image: '/onboarding/tour-deallink.png',
    body: "When you're ready to share — with a lender, a capital partner, a co-GP, a broker — click Send Deal Link. Copy the link, send it however you want. Recipients see a clean institutional read of the deal, can run their own scenarios, and provide feedback. No back-and-forth Excel. No version control nightmares. Just a link.",
  },
  {
    headline: 'Start with one deal.',
    centered: true,
    body: (
      <>
        Open the Builder. Describe a deal you&apos;re working on. Watch Arcanum
        render the institutional model. Run your first Deal Link to a partner.
        Build your pipeline.
        <br />
        <br />
        Direct line if you need anything: pope@arcanumhq.com. Founding members
        shape what ships next — tell us what you need.
      </>
    ),
  },
];

function BulletItem({ label, text }: { label: string; text: string }) {
  return (
    <div className="flex gap-2.5 mt-3.5">
      <span className="text-accent font-body font-semibold shrink-0">/</span>
      <div>
        <span className="text-text-bright font-bold">{label}</span>
        <span className="text-text"> — {text}</span>
      </div>
    </div>
  );
}

export function WelcomeTour({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [mobile, setMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < 768
  );
  const skipRef = useRef<HTMLButtonElement>(null);
  const backRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  // Phase 3 § 3.3 — StrictMode ref-dedupe for welcome_tour_shown mount event.
  // Mirrors the <SentryPostHogIdentity> lastKeyRef precedent. Separate from
  // hasFiredWelcomeTourEvent (localStorage, once-per-browser for server-side
  // lifecycle event) — this ref is in-memory per component mount, which is
  // the correct scope for the PostHog event (one per actual tour display,
  // not one per browser-lifetime).
  const tourCaptureFiredRef = useRef(false);

  // Track viewport for the column-reverse footer below 768px.
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const finish = useCallback(() => {
    // Phase 3 § 3.3 — fire BEFORE onClose so the event isn't dropped if
    // the close triggers an unmount. Action-driven; no StrictMode guard needed.
    posthog.capture('welcome_tour_completed', {});
    markWelcomeTourSeen(user?.id);
    onClose();
  }, [onClose, user?.id]);
  const skip = useCallback(() => {
    markWelcomeTourSeen(user?.id);
    onClose();
  }, [onClose, user?.id]);

  // Esc + ←/→ keyboard nav.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        skip();
      }
      if (e.key === 'ArrowRight') {
        setStep((s) => Math.min(STEPS.length - 1, s + 1));
      }
      if (e.key === 'ArrowLeft') {
        setStep((s) => Math.max(0, s - 1));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [skip]);

  // Mount effect — focus the Next button, lock body scroll, preload images.
  useEffect(() => {
    nextRef.current?.focus();
    document.body.style.overflow = 'hidden';
    STEPS.forEach((s) => {
      if (s.image) {
        const img = new Image();
        img.src = s.image;
      }
    });
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Fire WELCOME_TOUR_SHOWN exactly once per browser. The event-fired flag is
  // orthogonal to hasSeenWelcomeTour — the Replay path clears `hasSeen` but
  // leaves event_fired intact, so this useEffect's gate stays closed on
  // replays. Defensive optimistic write: localStorage.set BEFORE the api.post
  // so React strict-mode's double-mount can't double-fire (second mount sees
  // event_fired=true and short-circuits).
  //
  // Phase 3 § 3.3: also fires the parallel PostHog mirror (welcome_tour_shown,
  // lowercase snake_case). The PostHog event uses tourCaptureFiredRef (in-memory
  // per component mount) rather than the localStorage gate, so replays DO
  // re-fire the PostHog event — matching per-display funnel semantics — while
  // the server-side lifecycle event stays once-per-browser per spec § 3 AC.
  useEffect(() => {
    // Server-side lifecycle event: once per browser.
    if (!hasFiredWelcomeTourEvent() && user?.id) {
      markWelcomeTourEventFired();
      void api
        .post<{ ok: true }>('/api/users/me/lifecycle-events', {
          event_type: 'WELCOME_TOUR_SHOWN',
          payload: {
            user_id: user.id,
            first_shown_at: new Date().toISOString(),
            version: 'v1',
          },
        })
        .catch(() => {
          // Telemetry-write failure is non-fatal — the user's tour experience
          // is unaffected. We don't retry; per-browser-once is the v1 contract.
        });
    }
    // PostHog mirror: once per component mount (StrictMode ref-dedupe).
    if (tourCaptureFiredRef.current) return;
    tourCaptureFiredRef.current = true;
    posthog.capture('welcome_tour_shown', {});
  }, [user?.id]);

  const def = STEPS[step];
  const isFirst = step === 0;
  const isLast = step === STEPS.length - 1;
  const headlineSize = isFirst ? (mobile ? 24 : 32) : mobile ? 20 : 24;
  const padV = mobile ? 32 : 56;
  const padH = mobile ? 24 : 64;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Founding member onboarding tour, step ${step + 1} of ${
        STEPS.length
      }`}
      className="fixed inset-0 z-[10000] flex items-center justify-center backdrop-blur-md p-4"
      // Inline rgba kept per apps/web/CLAUDE.md audit-gate carve-out: there's
      // no Tailwind token at this exact tint, and the backdrop blur effect
      // depends on the alpha value.
      style={{
        background: 'rgba(10, 15, 13, 0.85)',
        animation: 'wt-fade 320ms ease-out',
      }}
    >
      <div
        className="relative w-full bg-void border border-accent/30 flex flex-col overflow-hidden font-body"
        style={{
          maxWidth: 720,
          height: '88vh',
          borderRadius: 12,
          padding: `${padV}px ${padH}px`,
          animation: 'wt-fade 320ms ease-out',
        }}
      >
        {/* Top-right skip */}
        <button
          ref={skipRef}
          onClick={skip}
          className="absolute font-body text-text hover:text-accent transition-colors cursor-pointer bg-transparent border-0"
          style={{
            top: 16,
            right: 20,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
          }}
        >
          Skip tour
        </button>

        {/* Step indicator bars */}
        <div className="flex justify-center mb-3" style={{ gap: 6 }}>
          {STEPS.map((_, i) => {
            const cls =
              i === step
                ? 'bg-accent'
                : i < step
                ? 'bg-accent/50'
                : 'bg-text-dim/40';
            return (
              <div
                key={i}
                className={`${cls} transition-colors`}
                style={{ width: 32, height: 2, borderRadius: 1 }}
              />
            );
          })}
        </div>
        <div
          className="text-center text-accent font-body"
          style={{
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            marginBottom: 24,
          }}
        >
          Step {step + 1} of {STEPS.length}
        </div>

        {/* Scrollable content */}
        <div
          key={step}
          className="flex-1 overflow-y-auto"
          style={{ animation: 'wt-stepfade 220ms ease-out', paddingRight: 4 }}
        >
          {def.eyebrow && (
            <div
              className="text-center text-accent font-body"
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.18em',
                marginBottom: 16,
              }}
            >
              {def.eyebrow}
            </div>
          )}
          {def.label && (
            <div
              className="text-accent font-body"
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                marginBottom: 12,
              }}
            >
              {def.label}
            </div>
          )}
          <h2
            className="font-serif italic font-medium text-text-bright"
            style={{
              margin: 0,
              fontSize: headlineSize,
              letterSpacing: '-0.02em',
              lineHeight: 1.15,
              textAlign: def.centered ? 'center' : 'left',
            }}
          >
            {def.headline}
          </h2>

          {(def.image || def.imageCaption) && (
            <div style={{ margin: '24px 0' }}>
              {def.image ? (
                <img
                  src={def.image}
                  alt={def.label || def.headline}
                  className="block w-full h-auto border border-accent/20"
                  style={{ borderRadius: 4, imageRendering: 'auto' }}
                />
              ) : (
                <div
                  className="w-full bg-void border border-accent/20 flex items-center justify-center text-text-muted font-body text-center"
                  style={{
                    aspectRatio: '16 / 9',
                    borderRadius: 4,
                    fontSize: 11,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    padding: 12,
                  }}
                >
                  {def.imageCaption}
                </div>
              )}
            </div>
          )}

          {def.body && (
            <div
              className="font-body text-text"
              style={{
                marginTop: def.image || def.imageCaption ? 0 : 32,
                fontSize: 14,
                lineHeight: 1.6,
                maxWidth: def.centered ? 540 : '100%',
                marginLeft: def.centered ? 'auto' : 0,
                marginRight: def.centered ? 'auto' : 0,
                textAlign: def.centered ? 'center' : 'left',
              }}
            >
              {def.body}
            </div>
          )}

          {isLast && (
            <div className="flex justify-center" style={{ marginTop: 32 }}>
              <button
                onClick={finish}
                className="bg-accent text-void font-body cursor-pointer border-0 hover:brightness-110 transition-[filter]"
                style={{
                  borderRadius: 4,
                  padding: '12px 28px',
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                }}
              >
                Get Started
              </button>
            </div>
          )}
        </div>

        {/* Footer — column-reverse on mobile (CSS prop, not class — depends on JS `mobile` state) */}
        <div
          className="border-t border-panel-border"
          style={{
            marginTop: 32,
            paddingTop: 32,
            display: 'flex',
            flexDirection: mobile ? 'column-reverse' : 'row',
            alignItems: mobile ? 'stretch' : 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <button
            onClick={skip}
            className="bg-transparent border-0 text-text hover:text-accent transition-colors font-body cursor-pointer"
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              padding: 0,
              textAlign: mobile ? 'center' : 'left',
            }}
          >
            Skip tour
          </button>
          <div
            style={{
              display: 'flex',
              flexDirection: mobile ? 'column-reverse' : 'row',
              gap: 10,
              alignItems: 'stretch',
            }}
          >
            <button
              ref={backRef}
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              className="bg-transparent text-accent font-body transition-all"
              style={{
                border: '1.5px solid var(--color-accent)',
                borderRadius: 4,
                padding: '9px 22px',
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                cursor: step === 0 ? 'not-allowed' : 'pointer',
                opacity: step === 0 ? 0.35 : 1,
              }}
            >
              Back
            </button>
            {!isLast && (
              <button
                ref={nextRef}
                onClick={() =>
                  setStep((s) => Math.min(STEPS.length - 1, s + 1))
                }
                className="bg-accent text-void border-0 font-body cursor-pointer hover:brightness-110 transition-[filter]"
                style={{
                  borderRadius: 4,
                  padding: '10px 24px',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                }}
              >
                Next
              </button>
            )}
            {isLast && (
              <button
                ref={nextRef}
                onClick={finish}
                className="bg-accent text-void border-0 font-body cursor-pointer hover:brightness-110 transition-[filter]"
                style={{
                  borderRadius: 4,
                  padding: '10px 24px',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                }}
              >
                Get Started
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
